/**
 * codCampaignGuard — a staff COD order for a customer in a LIVE "No COD" campaign.
 *
 * The storefront refuses COD for these customers outright (server/lib/campaignCodOff.js).
 * The office can still have a real reason to take one (a phone order the owner agreed to),
 * so createOrder WARNS and asks, instead of blocking: the member of staff must confirm,
 * and a confirmed order is stamped `codCampaignOverride` so it can be found later.
 *
 * Pure: the proxy call and the confirm dialog are passed in. A failed lookup never blocks
 * an order (the desk works as it did before this existed).
 *
 * @param {object} a
 * @param {string}  a.mobile
 * @param {boolean} a.isCod
 * @param {(mobile10: string) => Promise<{codOff:boolean, campaign?:{id,title,endsOn}}>} a.fetchCodOff
 * @param {(message: string) => boolean|Promise<boolean>} [a.confirm]  absent → warn only
 * @param {string} [a.agentName]
 * @returns {Promise<{ proceed: boolean, error?: string, warning?: string, stamp?: object }>}
 */
export async function checkCodCampaign({ mobile, isCod, fetchCodOff, confirm, agentName = '', now = () => new Date().toISOString() }) {
    const m10 = String(mobile || '').replace(/\D/g, '').slice(-10);
    if (!isCod || m10.length !== 10 || typeof fetchCodOff !== 'function') return { proceed: true };
    let res;
    try { res = await fetchCodOff(m10); } catch (e) {
        console.warn('[codCampaignGuard] lookup failed (order not blocked):', e?.message || e);
        return { proceed: true };
    }
    if (!res?.codOff || !res.campaign) return { proceed: true };
    const c = res.campaign;
    const warning = `This customer is in the campaign “${c.title}”, which has Cash on Delivery turned OFF`
        + `${c.endsOn ? ` until ${c.endsOn}` : ''}. The online store only lets them pay online.`;
    const stamp = { campaignId: c.id, title: c.title, endsOn: c.endsOn || null, confirmedBy: agentName || '', at: now() };
    if (typeof confirm !== 'function') return { proceed: true, warning, stamp };
    const ok = await confirm(`${warning}\n\nCreate this COD order anyway?`);
    if (!ok) return { proceed: false, error: `${warning} Order not created — switch it to Prepaid, or confirm the COD.` };
    return { proceed: true, warning, stamp };
}
